import React, { useState } from 'react'
import { useInView } from 'react-intersection-observer'

import styled from 'styled-components'

import { GIFTS_INTERACTIVE_CONTAINER } from 'utils/testing/testIDs'

import { GiftType } from '../../helpers'
import { RECAP_XS_BREAKPOINT } from '../../styles'
import { GiftShape } from './GiftShape'
import { GiftSquirrel } from './GiftSquirrel'
import { GiftShapeType, createGiftMatrix } from './utils'

const Container = styled.div`
  align-items: center;
  background: #222b2d;
  border-radius: 20px;
  display: flex;
  height: 400px;
  justify-content: center;
  width: 400px;

  @media screen and (max-width: ${RECAP_XS_BREAKPOINT}px) {
    background: none;
    border-radius: 0;
    height: inherit;
    margin: 20px 10px;
    max-width: 400px;
  }

  @media screen and (max-width: 340px) {
    transform: scale(0.9);
  }
`

const SMALL_PHONE_MEDIA_QUERY = `@media screen and (max-height: 580px) and (max-width: ${RECAP_XS_BREAKPOINT}px) `

const Content = styled.div`
  column-gap: calc(16px / 3);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(3, 1fr);
  row-gap: 20px;
  width: 335px;

  @media screen and (max-width: ${RECAP_XS_BREAKPOINT}px) {
    column-gap: 0;
  }

  ${SMALL_PHONE_MEDIA_QUERY} {
    grid-template-rows: repeat(2, 1fr);
  }
`

const HiddenInSmallPhone = styled.div<{ index: number }>`
  ${SMALL_PHONE_MEDIA_QUERY} {
    display: ${({ index }) => (index > 7 ? 'none' : 'initial')};
  }
`

const Interactive = ({ gifts }: { gifts: GiftType[] }): React.ReactElement => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })
  const [giftMatrix, setGiftMatrix] = useState<GiftShapeType[]>(
    createGiftMatrix(gifts),
  )

  const toggleOverlay = () => {
    const overlay = document.getElementById('transparent-overlay')
    if (overlay) {
      const nextDisplay = overlay?.style.display === 'flex' ? 'none' : 'flex'
      overlay.style.display = nextDisplay
    }
  }

  const handleGiftClick = (index: number) => {
    const tempGiftMatrix = [...giftMatrix]
    const hasMessage = tempGiftMatrix[index].note.length > 0
    if (hasMessage) {
      toggleOverlay()
      tempGiftMatrix[index].messageDisplayed =
        !tempGiftMatrix[index].messageDisplayed
    }
    setGiftMatrix(tempGiftMatrix)
  }

  return (
    <Container data-testid={GIFTS_INTERACTIVE_CONTAINER} ref={ref}>
      {inView ? (
        <Content>
          {giftMatrix.map((gift, index) => (
            <HiddenInSmallPhone
              key={
                gift.squirrelIcon
                  ? `${gift.contributorName}-${gift.note}-${index}`
                  : `${gift.shape}-${index}`
              }
              index={index}
            >
              {gift.squirrelIcon ? (
                <GiftSquirrel
                  gift={gift}
                  index={index}
                  onGiftClick={handleGiftClick}
                />
              ) : (
                <GiftShape
                  color={gift.color}
                  index={index}
                  isFlipped={gift.isFlipped}
                  shape={gift.shape}
                />
              )}
            </HiddenInSmallPhone>
          ))}
        </Content>
      ) : null}
    </Container>
  )
}

export { Interactive }
